import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Check, Store } from "lucide-react";

const TYPES = [{ v: "stand", l: "Stand" }, { v: "animation", l: "Animation" }, { v: "concert", l: "Concert" }, { v: "restauration", l: "Restauration" }, { v: "autre", l: "Autre" }];
const TYPE_FR = { stand: "Stand", animation: "Animation", concert: "Concert", restauration: "Restauration", autre: "Autre" };

export default function EventPresence({ slug }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ presence_type: "stand", stand_name: "", note: "" });
  const { data } = useQuery({ queryKey: ["presence", slug], queryFn: async () => (await api.get(`/events/${slug}/presence`)).data });

  const declare = async () => {
    if (!user) { toast.error("Connectez-vous pour déclarer votre présence."); return; }
    try { await api.post(`/events/${slug}/presence`, f); toast.success("Présence déclarée — en attente de validation par l'organisateur."); setOpen(false); qc.invalidateQueries({ queryKey: ["presence", slug] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const count = data?.count || 0;
  const pros = data?.professionals || [];
  const inp = "w-full bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200 outline-none focus:border-amber-500/50";

  return (
    <div className="my-8 rounded-xl border border-[#272A34] bg-[#121418] p-6" data-testid="event-presence">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="font-serif text-xl text-amber-100 flex items-center gap-2"><Store className="w-5 h-5 text-amber-500" /> <span data-testid="presence-count">{count}</span> exposant{count > 1 ? "s" : ""} présent{count > 1 ? "s" : ""}</h3>
        {data?.my_status ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full bg-amber-950/40 text-amber-300 border border-amber-800/40" data-testid="presence-my-status">{data.my_status === "approved" ? "Présence confirmée" : data.my_status === "refused" ? "Non retenue" : "En attente"}</span>
        ) : (
          <button data-testid="presence-declare-btn" onClick={() => (user ? setOpen((o) => !o) : declare())} className="inline-flex items-center gap-2 bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/50 px-4 py-2 rounded-md text-sm"><Users className="w-4 h-4" /> Je serai présent</button>
        )}
      </div>

      {open && !data?.my_status && (
        <div className="space-y-3 mb-5 p-4 rounded-lg bg-[#0B0C0E] border border-[#272A34]" data-testid="presence-form">
          <select className={inp} data-testid="presence-type" value={f.presence_type} onChange={(e) => setF({ ...f, presence_type: e.target.value })}>{TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}</select>
          <input className={inp} placeholder="Nom / n° de stand (optionnel)" value={f.stand_name} onChange={(e) => setF({ ...f, stand_name: e.target.value })} />
          <input className={inp} placeholder="Petite description (optionnel)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          <button onClick={declare} data-testid="presence-submit" className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 text-stone-950 font-semibold px-5 py-2.5 rounded-md text-sm border border-amber-400/30"><Check className="w-4 h-4" /> Confirmer ma présence</button>
        </div>
      )}

      {pros.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="presence-list">
          {pros.map((p) => {
            const chip = (<span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-[#0B0C0E] text-stone-300 border border-[#272A34]">{p.pro_name}<span className="text-[10px] text-stone-500">{TYPE_FR[p.presence_type]}</span></span>);
            return p.pro_slug ? <a key={p.id} href={`/professionnels/${p.pro_slug}`} data-testid={`presence-pro-${p.id}`}>{chip}</a> : <span key={p.id} data-testid={`presence-pro-${p.id}`}>{chip}</span>;
          })}
        </div>
      )}
    </div>
  );
}
